import { CustomTypeMod, OperationResultType } from './InterfaceAndTypeUtil';
import { logDebug } from './LoggingUtil';

export interface IGlobalEntityLookupControlRef {
  lazyHalt: boolean, // waits lookup interval to complete
  _halt?: () => void,
  _completed?: boolean,
  _elapsedTime?: number, // milliseconds
}

export const globalEntityOnLoadCallback = <T>({
  globalEntityName,
  onLoadCallback,
  lookupControlRef = { lazyHalt: false },
  lookupTimeout = 300,
  lookupIntervalStep = 5,
  debug = true,
}: {
  globalEntityName: string,
  onLoadCallback: (res: CustomTypeMod<OperationResultType, {
    data: T,
  }>) => void,
  lookupControlRef?: IGlobalEntityLookupControlRef,
  lookupTimeout?: number, // seconds
  lookupIntervalStep?: number, // milliseconds
  debug?: boolean,
}) => {
  const globalScopeRef: any = window;

  let intervalHandler: any = null;

  lookupControlRef._completed = false;

  lookupControlRef._elapsedTime = 0;

  lookupControlRef._halt = () => {
    clearInterval(intervalHandler);

    if (lookupControlRef._completed) {
      return;
    }

    lookupControlRef._completed = true;

    return onLoadCallback({
      error: {
        code: 'GEOLCHLT',
        description: 'globalEntityOnLoadCallback halt',
        meta: {
          globalEntityName,
        }
      },
      data: globalScopeRef[globalEntityName] as T
    });
  };

  if (typeof globalScopeRef[globalEntityName] !== 'undefined') {
    lookupControlRef._completed = true;
    lookupControlRef._halt = undefined;

    return onLoadCallback({
      error: {
        code: '0'
      },
      data: globalScopeRef[globalEntityName] as T
    });
  }

  const lookupTimeoutMs = lookupTimeout * 1000;

  intervalHandler = setInterval(() => {
    if (debug) {
      logDebug(`lookupControlRef: ${JSON.stringify(lookupControlRef)}`);
    }

    if (lookupControlRef.lazyHalt) {
      clearInterval(intervalHandler);

      lookupControlRef._completed = true;
      lookupControlRef._halt = undefined;

      return onLoadCallback({
        error: {
          code: 'GEOLCLHLT',
          description: 'globalEntityOnLoadCallback lazy halt',
          meta: {
            globalEntityName,
          }
        },
        data: globalScopeRef[globalEntityName] as T
      });
    }

    if (typeof globalScopeRef[globalEntityName] !== 'undefined') {
      clearInterval(intervalHandler);

      lookupControlRef._completed = true;
      lookupControlRef._halt = undefined;

      return onLoadCallback({
        error: {
          code: '0'
        },
        data: globalScopeRef[globalEntityName] as T
      });
    }

    lookupControlRef._elapsedTime! += lookupIntervalStep;

    if (lookupControlRef._elapsedTime! >= lookupTimeoutMs) {
      clearInterval(intervalHandler);

      lookupControlRef._completed = true;
      lookupControlRef._halt = undefined;

      return onLoadCallback({
        error: {
          code: 'GEOLCTO',
          description: 'globalEntityOnLoadCallback timeout',
          meta: {
            globalEntityName,
            lookupTimeout,
          }
        },
        data: globalScopeRef[globalEntityName] as T
      });
    }
  }, lookupIntervalStep);
};

export const globalEntityOnLoad = async <T>({
  globalEntityName,
  lookupControlRef = { lazyHalt: false },
  lookupTimeout = 300,
  lookupIntervalStep = 5,
}: {
  globalEntityName: string,
  lookupControlRef?: IGlobalEntityLookupControlRef,
  lookupTimeout?: number, // seconds
  lookupIntervalStep?: number, // milliseconds
}): Promise<CustomTypeMod<OperationResultType, {
  data: T,
}>> => {
  return new Promise((resolve) => {
    globalEntityOnLoadCallback<T>({
      globalEntityName,
      onLoadCallback: resolve,
      lookupControlRef,
      lookupTimeout,
      lookupIntervalStep,
    });
  });
};

export interface IEntityLookupControlRef extends IGlobalEntityLookupControlRef {};

export interface IEntityOnLoadCallbackParams<T> {
  scope?: any,
  entityName: string,
  subEntityName?: string,
  onLoadCallback: (res: CustomTypeMod<OperationResultType, {
    data: T,
  }>) => void,
  lookupControlRef?: IEntityLookupControlRef,
  lookupTimeout?: number, // milliseconds
  lookupIntervalStep?: number, // milliseconds
  debug?: boolean,
}

export interface IEntityOnLoadParams<T> extends Omit<IEntityOnLoadCallbackParams<T>, 'onLoadCallback' | 'debug'> {}

/**
 * Polls a scope object until a named entity becomes available, then invokes the callback.
 * Generalizes globalEntityOnLoadCallback by allowing any scope (defaults to window).
 *
 * When subEntityName is set, waits for scope[entityName][subEntityName] instead of scope[entityName].
 * Polls every lookupIntervalStep ms until the entity is found, lookupTimeout is reached,
 * lazyHalt is triggered, or lookupControlRef._halt() is called manually.
 *
 * Callback receives { error: { code: '0' } } on success; non-zero codes indicate halt, lazy halt, or timeout.
 */
export const entityOnLoadCallback = <T>({
  scope = window as any,
  entityName,
  subEntityName,
  onLoadCallback,
  lookupControlRef = { lazyHalt: false },
  lookupTimeout = 300000,
  lookupIntervalStep = 5,
  debug = true,
}: IEntityOnLoadCallbackParams<T>) => {
  const scopeRef: any = scope;

  const _getEntity = (): T | undefined => {
    const mainEntity = scopeRef[entityName];

    if (typeof mainEntity === 'undefined') {
      return undefined;
    }

    if (subEntityName) {
      const subEntity = mainEntity[subEntityName];

      if (typeof subEntity === 'undefined') {
        return undefined;
      }

      return subEntity as T;
    }

    return mainEntity as T;
  };

  const errorMeta = {
    entityName,
    subEntityName,
  };

  let intervalHandler: any = null;

  lookupControlRef._completed = false;

  lookupControlRef._elapsedTime = 0;

  lookupControlRef._halt = () => {
    clearInterval(intervalHandler);

    if (lookupControlRef._completed) {
      return;
    }

    lookupControlRef._completed = true;

    return onLoadCallback({
      error: {
        code: 'EOLCHLT',
        description: 'entityOnLoadCallback halt',
        meta: errorMeta,
      },
      data: _getEntity() as T
    });
  };

  if (typeof _getEntity() !== 'undefined') {
    lookupControlRef._completed = true;
    lookupControlRef._halt = undefined;

    return onLoadCallback({
      error: {
        code: '0'
      },
      data: _getEntity() as T
    });
  }

  const lookupTimeoutMs = lookupTimeout;

  intervalHandler = setInterval(() => {
    if (debug) {
      logDebug(`lookupControlRef: ${JSON.stringify(lookupControlRef)}`);
    }

    if (lookupControlRef.lazyHalt) {
      clearInterval(intervalHandler);

      lookupControlRef._completed = true;
      lookupControlRef._halt = undefined;

      return onLoadCallback({
        error: {
          code: 'EOLCLHLT',
          description: 'entityOnLoadCallback lazy halt',
          meta: errorMeta,
        },
        data: _getEntity() as T
      });
    }

    if (typeof _getEntity() !== 'undefined') {
      clearInterval(intervalHandler);

      lookupControlRef._completed = true;
      lookupControlRef._halt = undefined;

      return onLoadCallback({
        error: {
          code: '0'
        },
        data: _getEntity() as T
      });
    }

    lookupControlRef._elapsedTime! += lookupIntervalStep;

    if (lookupControlRef._elapsedTime! >= lookupTimeoutMs) {
      clearInterval(intervalHandler);

      lookupControlRef._completed = true;
      lookupControlRef._halt = undefined;

      return onLoadCallback({
        error: {
          code: 'EOLCTO',
          description: 'entityOnLoadCallback timeout',
          meta: {
            ...errorMeta,
            lookupTimeout,
          }
        },
        data: _getEntity() as T
      });
    }
  }, lookupIntervalStep);
};

/**
 * Async Promise wrapper around entityOnLoadCallback.
 * Resolves with the same result shape once the entity (or sub-entity) is available on the scope.
 */
export const entityOnLoad = async <T>({
  scope = window as any,
  entityName,
  subEntityName,
  lookupControlRef = { lazyHalt: false },
  lookupTimeout = 300000,
  lookupIntervalStep = 5,
}: IEntityOnLoadParams<T>): Promise<CustomTypeMod<OperationResultType, {
  data: T,
}>> => {
  return new Promise((resolve) => {
    entityOnLoadCallback<T>({
      scope,
      entityName,
      subEntityName,
      onLoadCallback: resolve,
      lookupControlRef,
      lookupTimeout,
      lookupIntervalStep,
    });
  });
};
